import type { NormalizedBridgeChartData } from "../types/data";
import type { BridgeChartVisibleDataExport, BridgeChartViewport } from "../types/public";

const MAX_EXPORT_ROWS = 10_000;
const encoder = new TextEncoder();

export function exportVisibleData(
  data: NormalizedBridgeChartData,
  viewport: BridgeChartViewport
): BridgeChartVisibleDataExport {
  validateViewport(data, viewport);
  const bars = data.bars.slice(viewport.start, viewport.end + 1);
  if (bars.length > MAX_EXPORT_ROWS) throw new RangeError("VisibleDataExportRowLimitExceeded");

  const indicators = data.indicators
    .filter(series => series.available)
    .sort((left, right) => left.code.localeCompare(right.code));
  const columns = ["date", "open", "high", "low", "close", "volume", ...indicators.map(series => series.code)];
  const rows = bars.map(bar => {
    validateBar(bar);
    return [
      bar.date,
      invariant(bar.open), invariant(bar.high), invariant(bar.low), invariant(bar.close), invariant(bar.volume),
      ...indicators.map(series => {
        const value = series.pointsByDate[bar.date];
        return value === undefined ? "" : invariant(value);
      })
    ].join(",");
  });
  const text = `${columns.join(",")}\n${rows.join("\n")}\n`;
  return Object.freeze({
    mediaType: "text/csv;charset=utf-8",
    fileName: `${safeFilePart(data.symbol)}-${safeFilePart(data.timeframe)}-visible.csv`,
    rowCount: bars.length,
    columns: Object.freeze(columns),
    bytes: encoder.encode(text)
  });
}

function validateViewport(data: NormalizedBridgeChartData, viewport: BridgeChartViewport): void {
  if (!Number.isInteger(viewport.start) || !Number.isInteger(viewport.end)) {
    throw new RangeError("VisibleDataExportViewportNotIntegral");
  }
  if (viewport.start < 0 || viewport.end < viewport.start || viewport.end >= data.bars.length) {
    throw new RangeError("VisibleDataExportViewportOutOfRange");
  }
}

function validateBar(bar: NormalizedBridgeChartData["bars"][number]): void {
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(bar.date)) throw new RangeError("VisibleDataExportDateInvalid");
  for (const value of [bar.open, bar.high, bar.low, bar.close, bar.volume]) invariant(value);
}

function invariant(value: number): string {
  if (!Number.isFinite(value)) throw new RangeError("VisibleDataExportValueNotFinite");
  return Object.is(value, -0) ? "0" : String(value);
}

function safeFilePart(value: string): string {
  const normalized = value.trim().replace(/[^A-Za-z0-9._-]+/gu, "-");
  return normalized || "chart";
}
